import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

type Role = 'admin' | 'vendor' | 'organizer';

const roles = [
  {
    id: 'admin' as Role,
    label: 'Administrateur',
    icon: 'ri-shield-user-line',
    description: 'Gérer la plateforme, approuver les contenus',
    color: 'from-navy to-navy-light',
    bg: 'bg-navy/10',
    border: 'border-navy',
    textColor: 'text-navy',
    email: 'admin@marketplace.cm',
    password: 'admin123',
  },
  {
    id: 'vendor' as Role,
    label: 'Vendeur',
    icon: 'ri-store-2-line',
    description: 'Vendre vos produits, suivre vos commandes',
    color: 'from-emerald-500 to-teal-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-500',
    textColor: 'text-emerald-700',
    email: 'aminata@shop.cm',
    password: 'vendor123',
  },
  {
    id: 'organizer' as Role,
    label: 'Organisateur',
    icon: 'ri-calendar-event-line',
    description: 'Créer vos événements, vendre vos tickets',
    color: 'from-gold to-gold-dark',
    bg: 'bg-gold-100',
    border: 'border-gold',
    textColor: 'text-gold-dark',
    email: 'jp@events.cm',
    password: 'org123',
  },
];

export default function Login() {
  const navigate = useNavigate();
  const logoSrc = 'https://static.readdy.ai/image/7e0624576d141773c913eba1868f652c/ea02af35c9a274d0014fddb0274b3cab.png';
  const { login, isAuthenticated, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    if (user.role === 'admin') navigate('/admin');
    else if (user.role === 'vendor') navigate('/vendor');
    else if (user.role === 'organizer') navigate('/organizer');
  }, [isAuthenticated, navigate, user]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) { setError('Veuillez remplir tous les champs.'); return; }
    setLoading(true);
    setError('');
    try {
      await login(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Email ou mot de passe incorrect.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FC] flex items-center justify-center p-4 sm:p-6 lg:p-10">
      <div className="w-full max-w-6xl bg-white rounded-[32px] shadow-2xl shadow-navy/10 overflow-hidden border border-white/60">
        <div className="grid lg:grid-cols-[0.95fr_1.05fr]">
          <div className="relative min-h-[260px] lg:min-h-[760px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=vibrant%20african%20marketplace%20scene%20colorful%20fabrics%20products%20vendors%20selling%20goods%20outdoor%20market%20modern%20africa%20bustling%20commerce%20lively%20atmosphere%20warm%20sunlight%20golden%20hour%20photography&width=900&height=1100&seq=login-bg-01&orientation=portrait"
              alt="Marketplace Africa"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-navy/80 via-navy/65 to-navy/35"></div>

            <div className="relative z-10 flex flex-col h-full p-6 sm:p-8 xl:p-10">
              <div className="flex items-center gap-3">
                <img src={logoSrc} alt="Foi Senegal" className="h-11 w-11 rounded-xl bg-white object-contain" />
                <div>
                  <span className="text-white font-bold text-lg tracking-tight">Foi Senegal</span>
                  <p className="text-white/60 text-xs">Vivre sa foi au sénégal</p>
                </div>
              </div>

              <div className="flex-1 flex flex-col justify-center py-8 lg:py-0">
                <div className="max-w-sm">
                  <div className="w-12 h-1 bg-gold rounded-full mb-6"></div>
                  <h2 className="text-3xl xl:text-5xl font-bold text-white leading-tight mb-4">
                    Commerce &<br />
                    <span className="text-gold">Événements</span><br />
                    Réunis
                  </h2>
                  <p className="text-white/75 text-sm xl:text-base leading-relaxed">
                    Vendez vos produits, organisez vos événements et gérez votre activité depuis une seule plateforme moderne et sécurisée.
                  </p>
                </div>
              </div>

              <div className="hidden md:grid grid-cols-3 gap-3">
                {[
                  { value: '5', label: 'Vendeurs actifs' },
                  { value: '2', label: 'Événements organisés' },
                  { value: '8', label: 'Transactions réalisées' },
                ].map((stat, i) => (
                  <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                    <p className="text-gold font-bold text-lg">{stat.value}</p>
                    <p className="text-white/60 text-[11px] mt-0.5">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center bg-[#F8F9FC] p-4 sm:p-8 xl:p-10">
            <div className="w-full max-w-lg bg-white rounded-[28px] shadow-[0_24px_70px_rgba(27,42,74,0.12)] border border-white/70 p-4 sm:p-8 xl:p-10">
              <div className="flex justify-center mb-5 sm:mb-6">
                <img
                  src={logoSrc}
                  alt="Logo"
                  className="w-16 h-16 sm:w-20 sm:h-20 object-contain rounded-2xl"
                />
              </div>

              <div className="mb-6 sm:mb-8">
                <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <Link to="/" className="text-xs font-bold uppercase tracking-[0.2em] text-navy/45 transition hover:text-navy">
                    Retour accueil
                  </Link>
                  <Link to="/register" className="text-sm font-semibold text-gold-dark transition hover:underline">
                    Déposer une candidature
                  </Link>
                </div>
                <h1 className="text-2xl xl:text-3xl font-bold text-navy mb-2 flex items-center gap-2">
                  Bienvenue
                  <span className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-hand-heart-line text-gold text-2xl"></i>
                  </span>
                </h1>
                <p className="text-gray-500 text-sm">Connectez-vous à votre espace de gestion</p>
              </div>

              <div className="transition-all duration-300">
                <p className="text-xs font-bold uppercase tracking-widest text-navy/50 mb-3">
                  Connexion
                </p>

                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-1.5">Adresse email</label>
                    <div className="relative">
                      <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="votre@email.com"
                        className="w-full pl-10 pr-4 py-3 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between mb-1.5">
                      <label className="block text-sm font-semibold text-navy">Mot de passe</label>
                      <button
                        type="button"
                        className="text-left text-xs text-gold-dark font-medium hover:underline cursor-pointer sm:text-right"
                        onClick={() => alert('Fonctionnalité bientôt disponible.')}
                      >
                        Mot de passe oublié ?
                      </button>
                    </div>
                    <div className="relative">
                      <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full pl-10 pr-11 py-3 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold transition-all"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-navy cursor-pointer"
                      >
                        <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-600 rounded-xl text-sm">
                      <i className="ri-error-warning-line flex-shrink-0"></i>
                      <span>{error}</span>
                    </div>
                  )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 rounded-xl font-bold text-white bg-navy hover:bg-navy-light transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 mt-2"
                >
                    {loading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Connexion en cours...
                      </>
                    ) : (
                      <>
                        <i className="ri-login-box-line"></i>
                        Se connecter
                      </>
                  )}
                </button>

                {/* <div className="rounded-2xl border border-gray-100 bg-[#F8F9FC] p-4">
                  <p className="mb-3 text-xs font-bold uppercase tracking-widest text-navy/45">
                    Accès selon votre compte
                  </p>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {roles.map((role) => (
                      <div key={role.id} className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white px-3 py-2.5">
                        <div className={`flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br ${role.color}`}>
                          <i className={`${role.icon} text-white text-base`}></i>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-navy">{role.label}</p>
                          <p className="truncate text-[11px] text-gray-400">{role.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div> */}

                <p className="text-center text-sm text-gray-400">
                  Pas encore de compte ?{' '}
                  <Link to="/register" className="font-semibold text-navy hover:underline">
                    S'inscrire
                  </Link>
                </p>
                </form>
              </div>

              <p className="text-xs text-gray-400 text-center mt-5 sm:mt-6 px-2">
                © 2026 Foi Senegal · Tous droits réservés
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
